import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import AppSelect from '@nxsys/components/AppSelect';
import AppSelectSync, { OptionType } from '@nxsys/components/AppSelectSync';
import AppSwitch from '@nxsys/components/AppSwitch';
import { irelandCountyOptions } from '@nxsys/constants/AppConstants';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import { showMessage } from '@nxsys/utils/message';
import { Option } from '@nxsys/types/models/Common';
import { countryList } from '@nxsys/utils';
import { Form } from 'antd';
import { FormProps } from 'antd/lib';
import { Ref, useImperativeHandle } from 'react';
import { useSelector } from 'react-redux';
import {
  formatPostCode,
  postCodeValidationRule,
} from '@nxsys/utils/formFieldValidation';

export type BranchFormValues = {
  siteName: string;
  code: string;
  VATCode: Option<string> | null;
  siteManager: Option<string> | null;
  companyName: string;
  address1: string;
  address2: string;
  address3: string;
  city: string;
  county: string;
  postCode: string;
  country: Option<string> | null;
  isSameAsAgency: boolean;
};

const defaultValues: BranchFormValues = {
  siteName: '',
  code: '',
  VATCode: null,
  siteManager: null,
  companyName: '',
  address1: '',
  address2: '',
  address3: '',
  city: '',
  county: '',
  postCode: '',
  country: null,
  isSameAsAgency: true,
};

export type BranchFormRef = {
  submit: () => void;
};

type BranchFormProps = {
  formRef?: Ref<BranchFormRef>;
  onSubmit?: (values: BranchFormValues) => void;
  defaultValues?: BranchFormValues;
  agencyId?: string;
};

const BranchForm: React.FC<BranchFormProps> = ({
  formRef,
  onSubmit,
  defaultValues: defaultValuesProps = {},
  agencyId,
}) => {
  const { messages } = useTypedIntl();
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);
  const isIreland = payrollCountry === IRELAND;

  const [form] = Form.useForm<BranchFormValues>();
  const watchValues = Form.useWatch([], form) ?? {};

  useImperativeHandle(formRef, () => ({
    submit() {
      form.submit();
    },
  }));

  const onFinish: FormProps<BranchFormValues>['onFinish'] = (values) => {
    onSubmit?.(values);
  };

  const onFinishFailed: FormProps<BranchFormValues>['onFinishFailed'] = (
    errorInfo,
  ) => {
    const fields = errorInfo?.errorFields
      ?.map((f) => f.errors?.[0])
      .filter(Boolean);
    if (fields?.length) {
      showMessage({ type: 'error', content: fields.join('. ') });
    }
  };

  return (
    <AppFormContainer
      form={form}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      scrollToFirstError
      layout="vertical"
      className="space-y-4 w-full"
      initialValues={{
        ...defaultValues,
        ...defaultValuesProps,
      }}
    >
      <Form.Item
        label={messages['common.name']}
        name="siteName"
        wrapperCol={{ span: 24 }}
      >
        <AppInput placeholder={messages['form.enterName']} />
      </Form.Item>
      <Form.Item
        label={messages['common.code']}
        name="code"
        wrapperCol={{ span: 24 }}
      >
        <AppInput placeholder={messages['form.enterCode']} />
      </Form.Item>
      <Form.Item
        label={messages['common.vatCode']}
        name="VATCode"
        wrapperCol={{ span: 24 }}
      >
        <AppSelectSync
          type="VATCode"
          apiType="searchVATCode"
          placeholder={messages['form.selectVATCode']}
          style={{ width: '100%' }}
          onChangeValue={(value: any) => {
            form.setFieldValue('VATCode', value);
          }}
        />
      </Form.Item>
      <Form.Item
        label={messages['common.siteManager']}
        name="siteManager"
        wrapperCol={{ span: 24 }}
      >
        <AppSelectSync
          type="agencyContact"
          apiType="searchAgencyContacts"
          searchId={agencyId}
          placeholder={messages['form.selectSiteManager']}
          style={{ width: '100%' }}
          onChangeValue={(value: OptionType | OptionType[] | null) => {
            form.setFieldValue('siteManager', value);
          }}
        />
      </Form.Item>
      <Form.Item
        label={messages['form.isSameInvoicingDetailAsAgencyDetails']}
        name="isSameAsAgency"
        valuePropName="checked"
        layout="horizontal"
        labelCol={{ span: 20 }}
        wrapperCol={{ span: 4 }}
      >
        <AppSwitch />
      </Form.Item>
      {!watchValues.isSameAsAgency && (
        <>
          <Form.Item
            label={messages['common.companyName']}
            name="companyName"
            wrapperCol={{ span: 24 }}
            rules={[
              {
                required: true,
              },
            ]}
          >
            <AppInput placeholder={messages['form.enter']} />
          </Form.Item>
          <Form.Item
            label={messages['common.address1']}
            name="address1"
            wrapperCol={{ span: 24 }}
          >
            <AppInput placeholder={messages['form.enter']} />
          </Form.Item>
          <Form.Item
            label={messages['common.address2']}
            name="address2"
            wrapperCol={{ span: 24 }}
          >
            <AppInput placeholder={messages['form.enter']} />
          </Form.Item>
          <Form.Item
            label={messages['common.address3']}
            name="address3"
            wrapperCol={{ span: 24 }}
          >
            <AppInput placeholder={messages['form.enter']} />
          </Form.Item>
          <Form.Item
            label={messages['common.cityTown']}
            name="city"
            wrapperCol={{ span: 24 }}
          >
            <AppInput placeholder={messages['form.enter']} />
          </Form.Item>
          <Form.Item
            label={messages['common.county']}
            name="county"
            wrapperCol={{ span: 24 }}
          >
            {isIreland ? (
              <AppSelect
                showSearch
                options={irelandCountyOptions}
                placeholder={messages['form.select']}
              />
            ) : (
              <AppInput placeholder={messages['form.enter']} />
            )}
          </Form.Item>
          <Form.Item
            label={messages[isIreland ? 'common.eirCode' : 'common.postCode']}
            name="postCode"
            wrapperCol={{ span: 24 }}
            rules={[
              { required: true, message: messages['form.pleaseEnter'] },
              postCodeValidationRule(messages, payrollCountry),
            ]}
          >
            <AppInput
              placeholder={messages['form.enter']}
              upperCaseAll
              onChange={(e) => {
                form.setFieldValue(
                  'postCode',
                  formatPostCode(e.target.value, payrollCountry),
                );
              }}
              maxLength={8}
            />
          </Form.Item>
          <Form.Item
            label={messages['common.country']}
            name="country"
            wrapperCol={{ span: 24 }}
          >
            <AppSelect
              placeholder={messages['form.selectCountry']}
              options={countryList}
            />
          </Form.Item>
        </>
      )}
    </AppFormContainer>
  );
};

export default BranchForm;
